import { FC, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { CountrySummary, TraderGrantDto } from "../../../api/Api";
import { useCountryOverview } from "../../countries/dataHooks/countryQueries";
import { CountryFlag } from "../../../components/countryFlag";
import { GrantDialog } from "../../../components/traderAccess/grantDialog";
import { GrantsTable } from "../../../components/traderAccess/grantsTable";
import { ExtendExpiryDialog } from "../../../components/traderAccess/extendExpiryDialog";
import { RevokeConfirmDialog } from "../../../components/traderAccess/revokeConfirmDialog";
import {
  useCountryTraderGrants,
  useGrantCountryTraderAccess,
  useRevokeCountryTraderAccess,
  useUpdateCountryTraderAccessExpiry
} from "./queries";

export const CountryTraderAccessAdmin: FC = () => {
  const [selectedCountry, setSelectedCountry] = useState<CountrySummary | null>(
    null
  );
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState<TraderGrantDto | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TraderGrantDto | null>(null);

  const countriesQuery = useCountryOverview("active");
  const countryOptions = useMemo(
    () => countriesQuery.data ?? [],
    [countriesQuery.data]
  );

  const countryId = selectedCountry?.id;

  const grantsQuery = useCountryTraderGrants(countryId, includeRevoked);
  const grants = grantsQuery.data ?? [];

  const grantMutation = useGrantCountryTraderAccess({
    onSuccess: () => setGrantOpen(false)
  });
  const revokeMutation = useRevokeCountryTraderAccess({
    onSuccess: () => setRevokeTarget(null)
  });
  const updateExpiryMutation = useUpdateCountryTraderAccessExpiry({
    onSuccess: () => setExtendTarget(null)
  });

  const handleClearExpiry = (row: TraderGrantDto) => {
    if (!row.entityId || !row.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: row.entityId,
      userObjectId: row.userObjectId,
      expiresAt: null
    });
  };

  const handleExtendSubmit = (expiresAt: string | null) => {
    if (!extendTarget?.entityId || !extendTarget.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: extendTarget.entityId,
      userObjectId: extendTarget.userObjectId,
      expiresAt
    });
  };

  const handleRevokeConfirm = () => {
    if (!revokeTarget?.entityId || !revokeTarget.userObjectId) return;
    revokeMutation.mutate({
      entityId: revokeTarget.entityId,
      userObjectId: revokeTarget.userObjectId
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Autocomplete<CountrySummary>
          sx={{ minWidth: 460 }}
          multiple={false}
          options={countryOptions}
          value={selectedCountry}
          onChange={(_, next) => setSelectedCountry(next)}
          getOptionLabel={(option) =>
            option.countryName
              ? `${option.countryName} - ${option.tradingTeam ?? ""}`
              : ""
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box display="flex" gap={1} alignItems="center" width="100%">
                <CountryFlag countryName={option.countryName} width={20} />
                <Typography variant="body2">{option.countryName}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.tradingTeam}
                </Typography>
              </Box>
            </li>
          )}
          renderValue={(v) => {
            return (
              <Box
                display="flex"
                gap={3}
                alignItems="center"
                paddingX="8px"
                height="100%"
              >
                <Box display="flex" gap={1} alignItems="center">
                  <CountryFlag countryName={v?.countryName} width={20} />
                  <Typography variant="body1">{v?.countryName}</Typography>
                </Box>
                -<Typography variant="body1">{v?.tradingTeam}</Typography>
              </Box>
            );
          }}
          loading={countriesQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              placeholder={selectedCountry ? "" : "Select a country"}
            />
          )}
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
            }
            label="Show revoked"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!countryId}
            onClick={() => setGrantOpen(true)}
          >
            Grant access
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {countryId ? (
          <GrantsTable
            rows={grants}
            loading={grantsQuery.isLoading || grantsQuery.isFetching}
            onExtendExpiry={setExtendTarget}
            onClearExpiry={handleClearExpiry}
            onRevoke={setRevokeTarget}
          />
        ) : (
          <Box sx={{ padding: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Select a country to manage trader access.
            </Typography>
          </Box>
        )}
      </Box>

      {countryId && (
        <GrantDialog
          open={grantOpen}
          entityId={countryId}
          onClose={() => setGrantOpen(false)}
          onSubmit={(body) => grantMutation.mutate(body)}
          isSubmitting={grantMutation.isPending}
        />
      )}

      <ExtendExpiryDialog
        open={extendTarget !== null}
        grant={extendTarget}
        onClose={() => setExtendTarget(null)}
        onSubmit={handleExtendSubmit}
        isSubmitting={updateExpiryMutation.isPending}
      />

      <RevokeConfirmDialog
        open={revokeTarget !== null}
        grant={revokeTarget}
        entityLabel="country"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        isSubmitting={revokeMutation.isPending}
      />
    </Box>
  );
};
